import {
  TARGET_STORY_VIDEO_BITRATE,
  shouldShrinkMobileVideo,
  shrinkDimensionConstraint,
} from "./upload-ux.util";
const SLOW_ENCODE_GRACE_MS = 12_000;
const HARD_CAP_MS = 4 * 60 * 1000;
const MIN_SIZE_RATIO = 0.85;

export type ShrinkMobileVideoOptions = {
  mobile: boolean;
  onProgress?: (percent: number) => void;
};

/**
 * Shrink a phone Camera Roll clip to 720p / 1.5 Mbps before Mux.
 * Returns the original file when the clip is already small, the browser cannot encode, or the copy is not smaller.
 */
export async function shrinkMobileCameraRoll(
  file: File,
  options: ShrinkMobileVideoOptions,
): Promise<File> {
  if (!file || !shouldShrinkMobileVideo(file.size, options.mobile)) {
    return file;
  }
  if (file.type && !file.type.startsWith("video/")) {
    return file;
  }
  if (typeof window === "undefined" || typeof (window as Window & { VideoEncoder?: unknown }).VideoEncoder === "undefined") {
    return file;
  }

  const duration = await readVideoDuration(file);
  if (!shouldShrinkMobileVideo(file.size, true, duration)) {
    return file;
  }

  try {
    return await transcode(file, duration, options.onProgress);
  } catch {
    return file;
  }
}

async function transcode(
  file: File,
  durationSeconds: number,
  onProgress?: (percent: number) => void,
): Promise<File> {
  const lib = await loadMediabunny();
  const input = new lib.Input({
    source: new lib.BlobSource(file),
    formats: lib.ALL_FORMATS,
  });

  try {
    const track = await input.getPrimaryVideoTrack();
    const width = Number(track?.displayWidth);
    const height = Number(track?.displayHeight);
    if (!track || !Number.isFinite(width) || !Number.isFinite(height) || width < 2 || height < 2) {
      return file;
    }
    const output = new lib.Output({
      format: new lib.Mp4OutputFormat({ fastStart: "in-memory" }),
      target: new lib.BufferTarget(),
    });
    const conversion = await lib.Conversion.init({
      input,
      output,
      video: {
        ...shrinkDimensionConstraint(width, height),
        frameRate: 30,
        codec: "avc",
        hardwareAcceleration: "prefer-hardware",
        quality: new lib.Quality({
          bitrate: TARGET_STORY_VIDEO_BITRATE,
          bitrateMode: "constant",
        }),
      },
    });
    if (!conversion.isValid) {
      return file;
    }

    let tooSlow = false;
    const started = Date.now();
    const hardCap = window.setTimeout(() => {
      tooSlow = true;
      void stopConversion(conversion);
    }, HARD_CAP_MS);
    conversion.onProgress = (progress: number) => {
      const ratio = Math.max(0, Math.min(1, Number(progress) || 0));
      onProgress?.(Math.round(ratio * 100));
      const elapsedSec = (Date.now() - started) / 1000;
      if (elapsedSec > SLOW_ENCODE_GRACE_MS / 1000 && durationSeconds > 0) {
        const producedSec = ratio * durationSeconds;
        if (producedSec < elapsedSec * 0.75) {
          tooSlow = true;
          void stopConversion(conversion);
        }
      }
    };

    try {
      await conversion.execute();
    } finally {
      window.clearTimeout(hardCap);
    }
    if (tooSlow) {
      return file;
    }

    const buffer = output.target?.buffer as ArrayBuffer | null | undefined;
    if (!buffer || buffer.byteLength <= 0 || buffer.byteLength >= file.size * MIN_SIZE_RATIO) {
      return file;
    }
    const stem = (file.name || "story-video").replace(/\.[^.]+$/, "") || "story-video";
    return new File([buffer], `${stem}.mp4`, { type: "video/mp4" });
  } finally {
    await releaseInput(input);
  }
}

function stopConversion(conversion: { cancel?: () => Promise<void> }): void {
  try {
    void conversion.cancel?.()?.catch(() => undefined);
  } catch {
    // The original file is uploaded if the encoder is stopped.
  }
}

const MEDIABUNNY_URL = "https://cdn.jsdelivr.net/npm/mediabunny@1.55.1/+esm";

async function loadMediabunny(): Promise<any> {
  // The staging SSR build fails if this URL is an import() specifier.
  // Build the import at runtime so webpack never tries to read the https scheme.
  const load = new Function(
    "url",
    "return import(url)",
  ) as (url: string) => Promise<any>;
  return load(MEDIABUNNY_URL);
}

function readVideoDuration(file: File): Promise<number> {
  if (typeof document === "undefined") {
    return Promise.resolve(0);
  }
  return new Promise((resolve) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    let settled = false;
    const finish = (value: number) => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timer);
      URL.revokeObjectURL(url);
      video.removeAttribute("src");
      video.load();
      resolve(Number.isFinite(value) && value > 0 ? value : 0);
    };
    const timer = window.setTimeout(() => finish(0), 4000);
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    video.onloadedmetadata = () => finish(video.duration);
    video.onerror = () => finish(0);
    video.src = url;
  });
}

async function releaseInput(input: { dispose?: () => void | Promise<void>; close?: () => void | Promise<void> }): Promise<void> {
  try {
    if (typeof input.dispose === "function") {
      await input.dispose();
      return;
    }
    if (typeof input.close === "function") {
      await input.close();
    }
  } catch {
    // The original file is still uploaded if cleanup fails.
  }
}
